import React, { useState, useEffect, useCallback } from "react";
import JobManager from "../components/JobManager";
import ServiceTree from "../components/ServiceTree";
import CreateUserModal from "../components/CreateUserModal";
import CopyUserModal from "../components/CopyUserModal";
import UserCellsManager from "../components/UserCellsManager";
import { useAuth } from "../context/ApiKeyContext";
import { Person, Email, Block, CheckCircle, FileCopy } from "@material-ui/icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
  Tabs,
  Tab,
  Box,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useTranslation } from "../hooks/useTranslation";
import { useUserManagement } from "../utils/userManagement";
import { getApiBaseUrl } from "../config";

const API_BASE_URL = getApiBaseUrl();

const UserManagement = () => {
  const { token, hasRole } = useAuth();
  const { t } = useTranslation("jobs");
  const { user } = useAuth();

  // Estados para notificações
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Função para mostrar notificações
  const showNotification = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Função para fechar notificação
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  // Hook para gerenciar companies e tenants
  const {
    companyOptions,
    loadingCompanies,
    selectedCompany,
    searchCompanies,
    selectCompany,
    tenantOptions,
    loadingTenants,
    selectedTenant,
    setSelectedCompany,
    setSelectedTenant,
    searchTenants,
    setTenantOptions,
  } = useUserManagement();
  // Estados para modal de criar usuário
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [copyUserModalOpen, setCopyUserModalOpen] = useState(false);
  const [copySourceUser, setCopySourceUser] = useState(null);

  // Estados para diálogo de detalhes
  const [selectedUser, setSelectedUser] = useState(null);
  const [userServices, setUserServices] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Estado para serviços e produtos (com regras aninhadas)
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [userProducts, setUserProducts] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Configuração das colunas da tabela específicas para serviços
  let columns = [
    {
      key: "id",
      label: t("table.id"),
      type: "text",
      sortable: true,
    },
    {
      key: "name",
      label: t("table.name"),
      type: "text",
      sortable: true,
    },
    {
      key: "email",
      label: t("table.email"),
      type: "text",
      sortable: true,
    },
  ];

  if (hasRole("ADMIN", "admin")) {
    columns.push({
      key: "company_name",
      label: t("table.company"),
      type: "text",
      sortable: true,
    });
  }
  columns = columns.concat([
    {
      key: "tenant_name",
      label: t("table.tenant"),
      type: "text",
      sortable: true,
    },
    {
      key: "creation_timestamp",
      label: t("table.creationTimestamp"),
      type: "date",
      sortable: true,
    },
    {
      key: "last_login",
      label: t("table.lastLogin"),
      type: "date",
      sortable: true,
    },
    {
      key: "status",
      label: t("table.status"),
      type: "status",
      sortable: true,
      formatValue: (value) => {
        return t(`status.${value.toUpperCase()}`) || value;
      },
    },
    {
      key: "actions",
      label: t("table.actions"),
      type: "actions",
      sortable: false,
    },
  ]);

  // Configuração dos campos de filtro específicos para serviços
  const filterFields = [
    {
      key: "name",
      label: t("filters.name"),
      type: "text",
      placeholder: t("filters.namePlaceholder"),
    },
    {
      key: "email",
      label: t("filters.email"),
      type: "text",
      placeholder: t("filters.emailPlaceholder"),
    },
    {
      key: "status",
      label: t("filters.status"),
      type: "select",
      options: [
        { value: "ACTIVE", label: t("status.ACTIVE") },
        { value: "PENDING", label: t("status.PENDING") },
      ],
    },
    {
      key: "include_inactive",
      label: t("filters.includeInactive"),
      type: "checkbox",
    },
    {
      key: "company",
      label: t("filters.company"),
      type: "autocomplete",
      placeholder: t("filters.companyPlaceholder"),
      options: companyOptions,
      getOptionLabel: (option) => option.name || "",
      loading: loadingCompanies,
      onInputChange: (newInputValue) => {
        if (newInputValue === "") {
          searchCompanies();
          // Limpar tenant quando company for limpa via X
          setSelectedTenant(null);
        } else if (newInputValue && newInputValue !== selectedCompany?.name) {
          searchCompanies(newInputValue);
        }
      },
      onChange: (newValue) => {
        selectCompany(newValue);
      },
      onClear: () => {
        setSelectedCompany(null);
        setSelectedTenant(null);
        setTenantOptions([]);
      },
    },
    {
      label: t("filters.tenant"),
      type: "autocomplete",
      placeholder: t("filters.tenantPlaceholder"),
      options: tenantOptions,
      getOptionLabel: (option) => option.name || "",
      loading: loadingTenants,
      disabled: !selectedCompany,
      key: `tenant-${selectedCompany?.id || "none"}`,
      onInputChange: (newInputValue) => {
        if (newInputValue === "" && selectedCompany) {
          searchTenants();
        } else if (
          newInputValue &&
          newInputValue !== selectedTenant?.name &&
          selectedCompany
        ) {
          searchTenants(newInputValue);
        }
      },
      onChange: (newValue) => {
        setSelectedTenant(newValue);
      },
      onClear: () => {
        setSelectedTenant(null);
      },
    },
  ];

  const handleDeactivateUser = async (userRow) => {
    if (
      !window.confirm(
        t("actions.deactivateConfirm", { name: userRow.name || userRow.email }),
      )
    ) {
      return;
    }
    try {
      const response = await fetch(
        `${API_BASE_URL}/users/${userRow.id}/deactivate`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          method: "PATCH",
        },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao desativar usuário");
      }
      showNotification(t("actions.deactivateSuccess"), "success");
      setRefreshTrigger((t) => t + 1);
    } catch (error) {
      showNotification(error.message || "Erro ao desativar usuário", "error");
    }
  };

  const handleReactivateUser = async (userRow) => {
    if (
      !window.confirm(
        t("actions.reactivateConfirm", { name: userRow.name || userRow.email }),
      )
    ) {
      return;
    }
    try {
      const response = await fetch(
        `${API_BASE_URL}/users/${userRow.id}/activate`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          method: "PATCH",
        },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao reativar usuário");
      }
      showNotification(t("actions.reactivateSuccess"), "success");
      setRefreshTrigger((t) => t + 1);
    } catch (error) {
      showNotification(error.message || "Erro ao reativar usuário", "error");
    }
  };

  const customActions = [
    {
      label: t("actions.copyUser"),
      tooltip: t("actions.copyUserTooltip"),
      icon: <FileCopy />,
      hidden: (job) =>
        !!job.inactivation_timestamp || job.id === user?.user_id,
      onClick: (userRow) => {
        setCopySourceUser(userRow);
        setCopyUserModalOpen(true);
      },
    },
    {
      label: t("actions.sendResetPasswordEmail"),
      tooltip: t("actions.sendResetPasswordEmailTooltip"),
      icon: <Email />,
      onClick: (userRow) => {
        handleSendResetPasswordEmail(userRow.email);
      },
    },
    {
      label: t("actions.reactivate"),
      tooltip: t("actions.reactivateTooltip"),
      icon: <CheckCircle />,
      hidden: (job) => !job.inactivation_timestamp || job.id === user?.user_id,
      onClick: (userRow) => handleReactivateUser(userRow),
    },
    {
      label: t("actions.deactivate"),
      tooltip: t("actions.deactivateTooltip"),
      icon: <Block />,
      hidden: (job) => !!job.inactivation_timestamp || job.id === user?.user_id,
      onClick: (userRow) => handleDeactivateUser(userRow),
    },
  ];

  const handleSendResetPasswordEmail = async (email) => {
    try {
      console.log(
        "Enviar email de redefinição de senha para o usuário:",
        email,
      );
      const response = await fetch(`${API_BASE_URL}/users/forgot_password`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        method: "POST",
        body: new URLSearchParams({ email }),
      });

      if (!response.ok) {
        throw new Error("Erro ao enviar email de redefinição de senha");
      }

      showNotification(
        "Email de redefinição de senha enviado com sucesso!",
        "success",
      );
    } catch (error) {
      console.error("Erro ao enviar email de redefinição:", error);
      showNotification("Erro ao enviar email de redefinição de senha", "error");
    }
  };

  // Função para buscar serviços (com perfis). for_user_id: ao abrir detalhes de outro usuário, para dependências.
  const fetchServices = useCallback(
    async (tenant_id = null, for_user_id = null) => {
      if (!tenant_id) {
        tenant_id = user?.tenant_id;
      }
      try {
        let url = `${API_BASE_URL}/users/services?tenant_id=${tenant_id}`;
        if (for_user_id != null) url += `&for_user_id=${for_user_id}`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok)
          throw new Error(`Erro ao buscar serviços: ${response.status}`);
        const data = await response.json();
        setServices(data.services || []);
        setProducts(data.products || []);
        return { services: data.services || [], products: data.products || [] };
      } catch (err) {
        console.error("Erro ao buscar serviços:", err);
      }
    },
    [token, user?.tenant_id],
  );

  // Handlers para ações
  const handleNewUser = () => {
    setCreateUserModalOpen(true);
  };

  const handleUserClick = async (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
    setLoadingUserDetails(true);
    const tenantId = user.tenant_id ?? user.tenantId;
    try {
      await Promise.all([
        tenantId ? fetchServices(tenantId, user.id) : Promise.resolve(),
        fetchUserDetails(user.id, true),
      ]);
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const handleUserAction = (action, user) => {
    console.log("Ação executada:", action, "no usuário:", user);

    switch (action) {
      case "view":
        handleUserClick(user);
        break;
      default:
        console.log("Ação desconhecida:", action);
    }
  };

  // Lida com clique no checkbox (regra de serviço)
  const handleRuleToggle = async (userId, service_rule_id, checked) => {
    try {
      if (checked) {
        await handleAssociateRule(userId, service_rule_id);
      } else {
        await handleDisassociateRule(userId, service_rule_id);
      }

      setUserServices((prevUserServices) => {
        const service = services.find((s) =>
          s.rules.some((rule) => rule.rule_id === service_rule_id),
        );
        if (!service) return prevUserServices;
        return prevUserServices.map((userService) => {
          if (userService.service_id === service.service_id) {
            const updatedRules = checked
              ? [...userService.rules, { rule_id: service_rule_id }]
              : userService.rules.filter(
                  (rule) => rule.rule_id !== service_rule_id,
                );
            return { ...userService, rules: updatedRules };
          }
          return userService;
        });
      });
    } catch (error) {
      console.error("Erro ao atualizar regra:", error);
      await fetchUserDetails(userId);
    }
  };

  // Lida com clique no checkbox (regra de produto)
  const handleProductRuleToggle = async (
    userId,
    product_code,
    rule_id,
    checked,
  ) => {
    try {
      if (checked) {
        await handleAssociateProductRule(userId, product_code, rule_id);
      } else {
        await handleDisassociateProductRule(userId, product_code, rule_id);
      }
      setUserProducts((prev) => {
        const next = prev.map((p) => {
          if (p.product_code !== product_code) return p;
          const rules = checked
            ? [...p.rules, { rule_id, rule_code: "", rule_name: "" }]
            : p.rules.filter((r) => r.rule_id !== rule_id);
          return { ...p, rules };
        });
        if (checked && !next.some((p) => p.product_code === product_code)) {
          const prod = products.find((pr) => pr.product_code === product_code);
          if (prod)
            next.push({
              product_code: prod.product_code,
              product_name: prod.product_name,
              rules: [{ rule_id, rule_code: "", rule_name: "" }],
            });
        }
        return next;
      });
    } catch (error) {
      console.error("Erro ao atualizar regra de produto:", error);
      await fetchUserDetails(userId);
    }
  };

  // Função para buscar detalhes do usuário (skipLoadingControl = true quando o chamador controla o loading)
  const fetchUserDetails = async (userId, skipLoadingControl = false) => {
    if (!skipLoadingControl) setLoadingUserDetails(true);

    try {
      // Buscar regras do usuário
      const rulesResponse = await fetch(
        `${API_BASE_URL}/users/rules/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      let rulesData = null;
      if (rulesResponse.ok) {
        rulesData = await rulesResponse.json();
        setUserServices(rulesData.services || []);
        setUserProducts(rulesData.products || []);
      } else {
        setUserServices([]);
        setUserProducts([]);
      }
    } catch (err) {
      console.error("Erro ao buscar detalhes do usuário:", err);
    } finally {
      if (!skipLoadingControl) setLoadingUserDetails(false);
    }
  };

  // Função para fechar diálogo
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setUserServices([]);
    setUserProducts([]);
    setActiveTab(0);
  };

  // Função para lidar com mudança de aba
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);

    // Se voltando da tab de células (1) para a tab de regras (0), fazer refresh
    if (activeTab === 1 && newValue === 0 && selectedUser) {
      fetchUserDetails(selectedUser.id);
    }
  };

  // Funções para modal de criar usuário
  const handleCloseCreateUserModal = () => {
    setCreateUserModalOpen(false);
  };

  const handleCloseCopyUserModal = () => {
    setCopyUserModalOpen(false);
    setCopySourceUser(null);
  };

  const handleUserCreated = () => {
    setRefreshTrigger((t) => t + 1);
  };

  // Função para associar regra
  const handleAssociateRule = async (user_id, service_rule_id) => {
    const rulesResponse = await fetch(`${API_BASE_URL}/users/associate_rule`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({ user_id, service_rule_id }),
    });
    if (!rulesResponse.ok) {
      throw new Error("Erro ao associar perfil");
    }
    showNotification("Regra associada com sucesso!", "success");
  };

  // Função para desassociar serviço
  const handleDisassociateService = async (user_id, service_id) => {
    const rulesResponse = await fetch(
      `${API_BASE_URL}/users/disassociate_service`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "DELETE",
        body: JSON.stringify({ user_id, service_id }),
      },
    );
    if (!rulesResponse.ok) {
      console.error("Erro ao desassociar serviço");
      throw new Error("Erro ao desassociar serviço");
    }
    showNotification("Serviço desassociado com sucesso!", "success");
  };

  // Função para associar serviço
  const handleAssociateService = async (user_id, service_id) => {
    const rulesResponse = await fetch(
      `${API_BASE_URL}/users/associate_service`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({ user_id, service_id }),
      },
    );
    if (!rulesResponse.ok) {
      console.error("Erro ao associar serviço");
      throw new Error("Erro ao associar serviço");
    }
    showNotification("Serviço associado com sucesso!", "success");
  };

  // Função para desassociar perfil (service rule)
  const handleDisassociateRule = async (user_id, service_rule_id) => {
    const rulesResponse = await fetch(
      `${API_BASE_URL}/users/disassociate_rule`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "DELETE",
        body: JSON.stringify({ user_id, service_rule_id }),
      },
    );
    if (!rulesResponse.ok) {
      console.error("Erro ao desassociar perfil");
      throw new Error("Erro ao desassociar perfil");
    }
    showNotification("Regra desassociada com sucesso!", "success");
  };

  // Função para associar regra de produto
  const handleAssociateProductRule = async (user_id, product_code, rule_id) => {
    const res = await fetch(`${API_BASE_URL}/users/associate_product_rule`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({ user_id, product_code, rule_id }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Erro ao associar regra de produto");
    }
    showNotification("Regra de produto associada com sucesso!", "success");
  };

  // Função para desassociar regra de produto
  const handleDisassociateProductRule = async (
    user_id,
    product_code,
    rule_id,
  ) => {
    const res = await fetch(`${API_BASE_URL}/users/disassociate_product_rule`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      method: "DELETE",
      body: JSON.stringify({ user_id, product_code, rule_id }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Erro ao desassociar regra de produto");
    }
    showNotification("Regra de produto desassociada com sucesso!", "success");
  };

  // Carregar dados iniciais (serviços e produtos do tenant)
  useEffect(() => {
    const tenantId = selectedTenant?.id ?? user?.tenant_id;
    if (tenantId) {
      fetchServices(tenantId);
    } else if (user?.tenant_id) {
      fetchServices(user.tenant_id);
    }
    if (hasRole("ADMIN", "admin")) {
      searchCompanies();
    }
  }, [
    fetchServices,
    user,
    user?.tenant_id,
    hasRole,
    searchCompanies,
    selectedTenant?.id,
  ]);

  // Sincronizar tenant quando company mudar
  useEffect(() => {
    if (selectedCompany) {
      // Limpar tenant selecionado quando company mudar
      setSelectedTenant(null);
      // Carregar tenants da nova company
      searchTenants();
    } else {
      // Se não há company selecionada, limpar tenant
      setSelectedTenant(null);
    }
  }, [selectedCompany, searchTenants, setSelectedTenant]);

  return (
    <>
      <JobManager
        jobType="usuarios"
        title={t("header.userManagement")}
        subtitle={t("header.userManagementSubtitle")}
        apiEndpoint="/users"
        createJobPath="/usuarios/novo"
        columns={columns}
        filterFields={filterFields}
        onNewJob={handleNewUser}
        onJobClick={handleUserClick}
        onJobAction={handleUserAction}
        newJobButtonLabel={t("header.newUser")}
        filterButtonLabel="Filtros"
        showNewJobButton={hasRole("USER_MANAGER", "user_manager")}
        dataPath="users"
        customActions={customActions}
        refreshTrigger={refreshTrigger}
        defaultFilters={{ include_inactive: false }}
      />

      {/* Diálogo de detalhes do usuário: altura fixa, uma única área de scroll, abas sempre visíveis */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            display: "flex",
            flexDirection: "column",
            height: "90vh",
            maxHeight: "90vh",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle className="user-mgmt-dialog-title">
          <div className="flex-row">
            <Person className="mr-8" />
            {t("header.userManagementTitle")} - {selectedUser?.name}
          </div>
        </DialogTitle>

        <DialogContent className="p-24">
          {loadingUserDetails ? (
            <div className="user-mgmt-loading-box">
              <CircularProgress />
            </div>
          ) : (
            <>
              <Typography
                variant="h6"
                gutterBottom
                className="font-bold mb-16"
              >
                {t("header.userManagementTitle")}
              </Typography>

              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                className="mb-16"
              >
                <Tab label={t("table.rules")} />
                <Tab label={t("table.cells")} />
              </Tabs>

              <Box style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 24 }}>
                {activeTab === 0 && (
                  <ServiceTree
                    services={services}
                    userServices={userServices}
                    products={products}
                    userProducts={userProducts}
                    selectedUser={selectedUser}
                    onRuleToggle={handleRuleToggle}
                    onProductRuleToggle={handleProductRuleToggle}
                    onServiceToggle={async (userId, serviceId, checked) => {
                      try {
                        if (checked) {
                          await handleAssociateService(userId, serviceId);
                        } else {
                          await handleDisassociateService(userId, serviceId);
                        }
                        fetchUserDetails(userId);
                      } catch (error) {
                        console.error("Erro ao atualizar serviço:", error);
                        fetchUserDetails(userId);
                      }
                    }}
                    hasRole={hasRole}
                  />
                )}

                {activeTab === 1 && selectedUser && (
                  <UserCellsManager
                    key={selectedUser.id} // Força re-render quando usuário mudar
                    userId={selectedUser.id}
                    token={token}
                    onCellsChange={(cells) => {
                      console.log("Células do usuário alteradas:", cells);
                    }}
                  />
                )}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions style={{ flexShrink: 0 }}>
          <Button onClick={handleCloseDialog} color="primary">
            {t("actions.close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de criar usuário */}
      <CreateUserModal
        open={createUserModalOpen}
        onClose={handleCloseCreateUserModal}
        onUserCreated={handleUserCreated}
        fetchServices={fetchServices}
        token={token}
      />

      <CopyUserModal
        open={copyUserModalOpen}
        onClose={handleCloseCopyUserModal}
        sourceUser={copySourceUser}
        token={token}
        onUserCreated={handleUserCreated}
      />

      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserManagement;
